import React, { useState } from 'react';
import { ExternalLink, Star, GitFork, Calendar } from 'lucide-react';
import { mockProjects } from '../mock';

const Projects = () => {
  const [filter, setFilter] = useState('all');

  const languages = ['all', ...new Set(mockProjects.map(p => p.language))];

  const filteredProjects = filter === 'all' 
    ? mockProjects 
    : mockProjects.filter(p => p.language === filter);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  return (
    <section id="projects" className="relative py-24">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">Projects</h2>
          <div className="w-20 h-1 bg-cyan-500 mx-auto"></div>
          <p className="text-white/60 mt-4 max-w-2xl mx-auto">
            Exploring AI/ML through hands-on projects and open-source contributions
          </p>
        </div>

        {/* Filter buttons */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {languages.map((lang) => (
            <button
              key={lang}
              onClick={() => setFilter(lang)}
              className={`px-4 py-2 rounded-lg font-medium transition-all duration-300 ${
                filter === lang
                  ? 'bg-cyan-500 text-black'
                  : 'bg-white/5 text-white/70 hover:bg-white/10 hover:text-white border border-white/10 hover:border-cyan-500/50'
              }`}
            >
              {lang === 'all' ? 'All Projects' : lang}
            </button>
          ))}
        </div>

        {/* Projects grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProjects.map((project, index) => (
            <div
              key={project.id}
              className="group bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg p-6 hover:bg-white/10 hover:border-cyan-500/50 transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-cyan-500/20"
              style={{
                animationDelay: `${index * 0.1}s`
              }}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-white mb-2 group-hover:text-cyan-400 transition-colors duration-300">
                    {project.name}
                  </h3>
                  <span className="inline-block px-2 py-1 bg-cyan-500/20 text-cyan-400 text-xs font-medium rounded border border-cyan-500/30">
                    {project.language}
                  </span>
                </div>
                <a
                  href={project.html_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white/60 hover:text-cyan-400 transition-colors duration-300"
                >
                  <ExternalLink size={20} />
                </a>
              </div>

              <p className="text-white/70 text-sm leading-relaxed mb-4">
                {project.description}
              </p>

              <div className="flex flex-wrap gap-2 mb-4">
                {project.topics.slice(0, 3).map((topic, idx) => (
                  <span
                    key={idx}
                    className="px-2 py-1 bg-white/5 text-white/60 text-xs rounded border border-white/10"
                  >
                    {topic}
                  </span>
                ))}
              </div>

              <div className="flex items-center justify-between text-white/50 text-sm pt-4 border-t border-white/10">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-1">
                    <Star size={16} />
                    <span>{project.stargazers_count}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Calendar size={16} />
                    <span>{formatDate(project.updated_at)}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-12">
          <a
            href="https://github.com/PB811"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-3 bg-white/5 hover:bg-cyan-500/20 text-white border border-white/10 hover:border-cyan-500/50 rounded-lg transition-all duration-300 hover:scale-105"
          >
            View All on GitHub
            <ExternalLink size={18} />
          </a>
        </div>
      </div>
    </section>
  );
};

export default Projects;